<div class="aca-sharelink__head">
  <div class="aca-sharelink__badge">
    <mat-icon>link</mat-icon>
  </div>
  <div class="aca-sharelink__titles">
    <h2 class="aca-sharelink__title">{{ 'DMS.SHARELINK.TITLE' | translate }}</h2>
    <div class="aca-sharelink__file" [title]="fileName">
      <mat-icon class="aca-sharelink__file-icon">description</mat-icon>
      <span>{{ fileName }}</span>
    </div>
  </div>
  <button mat-icon-button type="button" class="aca-sharelink__close" (click)="close()"
          [attr.aria-label]="'DMS.SHARELINK.CLOSE' | translate">
    <mat-icon>close</mat-icon>
  </button>
</div>

<mat-dialog-content class="aca-sharelink__body">
  <!-- Public link section -->
  <section class="aca-sharelink__card">
    <div class="aca-sharelink__row">
      <mat-icon class="aca-sharelink__row-icon">public</mat-icon>
      <div class="aca-sharelink__row-text">
        <div class="aca-sharelink__row-title">{{ 'DMS.SHARELINK.PUBLIC_LINK' | translate }}</div>
        <div class="aca-sharelink__row-sub">{{ 'DMS.SHARELINK.PUBLIC_HINT' | translate }}</div>
      </div>
      <mat-slide-toggle
        data-automation-id="aca-share-toggle"
        [checked]="isFileShared"
        [disabled]="!canUpdate || isBusy"
        (change)="toggleShare($event)" />
    </div>

    <div *ngIf="isFileShared" class="aca-sharelink__linkbox" data-automation-id="aca-share-linkbox">
      <mat-icon class="aca-sharelink__linkbox-icon">link</mat-icon>
      <input
        #shareLinkInput
        class="aca-sharelink__linkbox-input"
        data-automation-id="aca-share-link"
        readonly
        [value]="shareUrl"
        (focus)="shareLinkInput.select()"
        [attr.aria-label]="'DMS.SHARELINK.PUBLIC_LINK' | translate" />
      <button mat-flat-button type="button" class="aca-sharelink__copy" (click)="copyLink()"
              [class.aca-sharelink__copy--done]="copied">
        <mat-icon>{{ copied ? 'check' : 'content_copy' }}</mat-icon>
        <span>{{ (copied ? 'DMS.SHARELINK.COPIED' : 'DMS.SHARELINK.COPY') | translate }}</span>
      </button>
    </div>

    <ng-container *ngIf="isFileShared">
      <div class="aca-sharelink__divider"></div>

      <div class="aca-sharelink__row">
        <mat-icon class="aca-sharelink__row-icon">timer</mat-icon>
        <div class="aca-sharelink__row-text">
          <div class="aca-sharelink__row-title">{{ 'DMS.SHARELINK.EXPIRY' | translate }}</div>
          <div class="aca-sharelink__row-sub">{{ 'DMS.SHARELINK.EXPIRY_HINT' | translate }}</div>
        </div>
        <mat-slide-toggle
          data-automation-id="aca-expire-toggle"
          [checked]="expiryOn"
          [disabled]="!canUpdate || isBusy"
          (change)="toggleExpiry($event)" />
      </div>

      <div *ngIf="expiryOn" class="aca-sharelink__expiry">
        <mat-form-field class="aca-sharelink__field" data-automation-id="aca-share-expiry-field">
          <mat-label>{{ 'DMS.SHARELINK.EXPIRY_FIELD' | translate }}</mat-label>
          <input
            matInput
            [matDatepicker]="expiryPicker"
            [min]="minDate"
            [formControl]="expiryDate"
            (dateChange)="expiryChanged()"
            readonly />
          <mat-datepicker-toggle matIconSuffix [for]="expiryPicker" />
          <mat-datepicker #expiryPicker />
          <mat-error *ngIf="expiryDate.errors?.['invalidDate']">
            {{ 'DMS.SHARELINK.EXPIRY_INVALID' | translate }}
          </mat-error>
        </mat-form-field>
        <div class="aca-sharelink__note">
          <mat-icon>info_outline</mat-icon>
          <span>{{ 'DMS.SHARELINK.EXPIRY_NOTE' | translate }}</span>
        </div>
      </div>
    </ng-container>
  </section>

  <!-- Email section -->
  <section *ngIf="isFileShared" class="aca-sharelink__card" data-automation-id="aca-share-mail">
    <div class="aca-sharelink__row">
      <mat-icon class="aca-sharelink__row-icon">mail_outline</mat-icon>
      <div class="aca-sharelink__row-text">
        <div class="aca-sharelink__row-title">{{ 'DMS.SHARELINK.MAIL_TITLE' | translate }}</div>
        <div class="aca-sharelink__row-sub">{{ 'DMS.SHARELINK.MAIL_HINT' | translate }}</div>
      </div>
    </div>

    <mat-form-field class="aca-sharelink__field">
      <mat-label>{{ 'DMS.SHARELINK.MAIL_TO' | translate }}</mat-label>
      <input
        matInput
        type="text"
        [(ngModel)]="mailTo"
        (ngModelChange)="mailInvalid = false"
        placeholder="name@example.com, name2@example.com"
        data-automation-id="aca-share-mail-to" />
      <mat-icon matIconSuffix class="aca-sharelink__field-icon">alternate_email</mat-icon>
      <mat-error *ngIf="mailInvalid">{{ 'DMS.SHARELINK.MAIL_TO_INVALID' | translate }}</mat-error>
    </mat-form-field>

    <mat-form-field *ngIf="templates.length" class="aca-sharelink__field">
      <mat-label>{{ 'DMS.SHARELINK.MAIL_TEMPLATE' | translate }}</mat-label>
      <mat-select
        [ngModel]="selectedTemplateId"
        (ngModelChange)="applyTemplate($event)"
        data-automation-id="aca-share-mail-template">
        <mat-option value="">{{ 'DMS.SHARELINK.MAIL_TEMPLATE_NONE' | translate }}</mat-option>
        <mat-option *ngFor="let tpl of templates" [value]="tpl.id">{{ tpl.name }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="aca-sharelink__field">
      <mat-label>{{ 'DMS.SHARELINK.MAIL_SUBJECT' | translate }}</mat-label>
      <input matInput type="text" [(ngModel)]="mailSubject" data-automation-id="aca-share-mail-subject" />
    </mat-form-field>

    <mat-form-field class="aca-sharelink__field aca-sharelink__field--area">
      <mat-label>{{ 'DMS.SHARELINK.MAIL_BODY' | translate }}</mat-label>
      <textarea matInput rows="5" [(ngModel)]="mailBody" data-automation-id="aca-share-mail-body"></textarea>
    </mat-form-field>

    <div class="aca-sharelink__placeholders">{{ 'DMS.SHARELINK.MAIL_PLACEHOLDERS' | translate }}</div>

    <div class="aca-sharelink__mail-actions">
      <span *ngIf="mailSent" class="aca-sharelink__sent">
        <mat-icon>check_circle</mat-icon>{{ 'DMS.SHARELINK.MAIL_SENT' | translate }}
      </span>
      <button mat-flat-button color="primary" type="button" (click)="sendMail()" [disabled]="isBusy || mailSending"
              data-automation-id="aca-share-mail-send">
        <mat-spinner *ngIf="mailSending" diameter="16" />
        <mat-icon *ngIf="!mailSending">{{ mailSent ? 'check' : (sendsDirectly ? 'send' : 'open_in_new') }}</mat-icon>
        <span>{{ (sendsDirectly ? 'DMS.SHARELINK.MAIL_SEND_NOW' : 'DMS.SHARELINK.MAIL_SEND') | translate }}</span>
      </button>
    </div>
  </section>

  <div *ngIf="isBusy" class="aca-sharelink__busy">
    <mat-spinner diameter="20" />
    <span>{{ 'DMS.SHARELINK.WORKING' | translate }}</span>
  </div>
</mat-dialog-content>

<div mat-dialog-actions class="aca-sharelink__actions">
  <button mat-button type="button" (click)="close()" data-automation-id="aca-share-dialog-close">
    {{ 'DMS.SHARELINK.CLOSE' | translate }}
  </button>
</div>
